import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import {
  Check, Loader2, AlertCircle, ArrowLeft, ArrowRight, Minus, Plus,
  Upload, Paintbrush, Package, Shirt, ChevronDown, ChevronUp,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Size/Color Breakdown Grid ── */
const BreakdownGrid = ({ label, options, breakdown, total, onChange }) => {
  const allocated = breakdown.reduce((sum, b) => sum + b.quantity, 0);
  const remaining = total - allocated;

  const setQty = (code, qty) => {
    const newBd = breakdown.filter(b => b.code !== code);
    if (qty > 0) newBd.push({ code, quantity: qty });
    onChange(newBd);
  };

  const getQty = (code) => breakdown.find(b => b.code === code)?.quantity || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-xs font-bold uppercase tracking-wider text-[#4A90A4]" style={{ fontFamily: 'Outfit' }}>{label}</label>
        <span className={`text-xs font-semibold ${remaining === 0 ? 'text-[#34C759]' : remaining < 0 ? 'text-red-400' : 'text-[#F5A623]'}`}>
          {allocated}/{total} assigned
        </span>
      </div>
      <div className="space-y-1.5">
        {options.map(opt => {
          const qty = getQty(opt.code);
          const hasQty = qty > 0;
          return (
            <div key={opt.code}
              className={`flex items-center justify-between px-4 py-2.5 rounded-xl border transition-all
                ${hasQty ? 'border-[#F5A623]/30 bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29]'}`}>
              <div className="flex items-center gap-3">
                {opt.hex && (
                  <div className="w-5 h-5 rounded-full border border-white/20 flex-shrink-0"
                    style={{ backgroundColor: opt.hex }} />
                )}
                <span className="text-sm text-white" style={{ fontFamily: 'Manrope' }}>{opt.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {hasQty && (
                  <button onClick={() => setQty(opt.code, Math.max(0, qty - 1))}
                    className="w-7 h-7 rounded-lg bg-[#1E4159] text-[#A0B8C7] hover:bg-red-500/20 hover:text-red-400 flex items-center justify-center transition-all">
                    <Minus className="w-3 h-3" />
                  </button>
                )}
                {hasQty && (
                  <span className="w-8 text-center text-sm font-bold text-white" style={{ fontFamily: 'Outfit' }}>{qty}</span>
                )}
                <button onClick={() => setQty(opt.code, qty + 1)}
                  disabled={remaining <= 0 && !hasQty}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all
                    ${hasQty
                      ? 'bg-[#1E4159] text-[#A0B8C7] hover:bg-[#F5A623] hover:text-[#0A1C29]'
                      : remaining > 0
                        ? 'bg-[#F5A623]/10 text-[#F5A623] hover:bg-[#F5A623] hover:text-[#0A1C29]'
                        : 'bg-[#1E4159]/50 text-[#A0B8C7]/30 cursor-not-allowed'}`}>
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Logo Selector ── */
const LogoSelector = ({ logoType, logoUrl, onTypeChange, onUrlChange, orderId, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/api/uploads/customer`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onUrlChange(res.data.public_url);
      onTypeChange('upload');
      toast.success('Logo uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#4A90A4] mb-3 block" style={{ fontFamily: 'Outfit' }}>Logo / Artwork</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button onClick={() => { fileRef.current?.click(); }}
          className={`p-4 rounded-xl border-2 text-left transition-all ${logoType === 'upload' ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/40'}`}
          data-testid="logo-upload-btn">
          <div className="flex items-center gap-3 mb-1">
            <Upload className="w-5 h-5 text-[#4A90A4]" />
            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </span>
          </div>
          <p className="text-[10px] text-[#A0B8C7]/50">Upload an image file (PNG, JPG, SVG)</p>
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />

        <Link to={`/portal/orders/${orderId}/design`}
          className={`p-4 rounded-xl border-2 text-left transition-all ${logoType === 'design_builder' ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/40'}`}
          onClick={() => onTypeChange('design_builder')}
          data-testid="logo-design-btn">
          <div className="flex items-center gap-3 mb-1">
            <Paintbrush className="w-5 h-5 text-[#4A90A4]" />
            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>Create in Designer</span>
          </div>
          <p className="text-[10px] text-[#A0B8C7]/50">Use our Design Builder tool</p>
        </Link>
      </div>

      {logoType === 'upload' && logoUrl && (
        <div className="mt-3 flex items-center gap-3 p-3 bg-[#0A1C29] rounded-xl border border-[#1E4159]">
          <img src={logoUrl} alt="Logo" className="w-14 h-14 rounded-lg object-contain bg-white/5 border border-[#1E4159]" />
          <p className="text-xs text-[#A0B8C7] truncate flex-1">{logoUrl.split('/').pop()}</p>
          <Check className="w-4 h-4 text-[#34C759]" />
        </div>
      )}
    </div>
  );
};

/* ── Bundle Configurator (for school_uniform_bundle items) ── */
const BundleConfigurator = ({ bundleConfig, bundleItems, schoolSlug, totalQty, value, onChange }) => {
  const upgrades = bundleConfig?.upgrades || [];
  const v = value || { upgrades: [], item_selections: [] };
  // Northwest Academy bundles require color selection on every child item.
  const requireColors = (schoolSlug || '').toLowerCase().includes('northwest');

  // Build the expanded per-instance list: every bundle item × its quantity ×
  // the parent order quantity gives one slot the customer must fill.
  // e.g. bundle item "Polo × 2" inside an order of 3 bundles → 6 slots.
  const slots = React.useMemo(() => {
    const out = [];
    (bundleItems || []).forEach((bi) => {
      const perBundleQty = bi.quantity || 1;
      for (let b = 0; b < (totalQty || 1); b += 1) {
        for (let i = 0; i < perBundleQty; i += 1) {
          out.push({
            key: `${bi.product_id}-${b}-${i}`,
            product_id: bi.product_id,
            name: bi.name,
            sizes: bi.sizes || [],
            colors: bi.colors || [],
            bundle_index: b,
            slot_index: i,
          });
        }
      }
    });
    return out;
  }, [bundleItems, totalQty]);

  const itemSelections = v.item_selections || [];

  const setSelection = (key, patch) => {
    const next = [...itemSelections];
    const idx = next.findIndex((s) => s.key === key);
    if (idx >= 0) next[idx] = { ...next[idx], ...patch };
    else next.push({ key, ...patch });
    onChange({ ...v, item_selections: next });
  };
  const getSelection = (key) => itemSelections.find((s) => s.key === key) || {};

  const setUpgrade = (label, patch) => {
    const next = [...(v.upgrades || [])];
    const idx = next.findIndex((u) => u.label === label);
    if (idx >= 0) next[idx] = { ...next[idx], ...patch };
    else next.push({ label, selected: false, text_value: null, ...patch });
    onChange({ ...v, upgrades: next });
  };
  const getUpgrade = (label) => (v.upgrades || []).find((u) => u.label === label) || { selected: false, text_value: '' };

  return (
    <div className="space-y-5" data-testid="bundle-configurator">
      {slots.length > 0 && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#4A90A4] mb-2 block" style={{ fontFamily: 'Outfit' }}>
            Choose a size {requireColors ? 'and color ' : ''}for each item
          </label>
          <div className="space-y-2">
            {slots.map((slot) => {
              const sel = getSelection(slot.key);
              const hasMultiBundles = (totalQty || 1) > 1;
              return (
                <div key={slot.key}
                  className="rounded-xl border border-[#1E4159] bg-[#0A1C29] p-4"
                  data-testid={`bundle-slot-${slot.key}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
                      {slot.name}
                      {hasMultiBundles && (
                        <span className="ml-2 text-[10px] text-[#A0B8C7]/60 uppercase tracking-wider font-bold">
                          Bundle #{slot.bundle_index + 1}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={`grid ${requireColors && slot.colors.length > 0 ? 'grid-cols-1 sm:grid-cols-2 gap-3' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-[10px] uppercase font-bold tracking-wider text-[#A0B8C7]/70 mb-1.5">Size</label>
                      <select
                        value={sel.size_code || ''}
                        onChange={(e) => setSelection(slot.key, { size_code: e.target.value, product_id: slot.product_id })}
                        className="w-full px-3 py-2 rounded-lg bg-[#0F2A3D] border border-[#1E4159] text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                        data-testid={`bundle-slot-${slot.key}-size`}
                      >
                        <option value="">— Select size —</option>
                        {(slot.sizes.length > 0 ? slot.sizes : ['XS','S','M','L','XL','2XL']).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    {requireColors && slot.colors.length > 0 && (
                      <div>
                        <label className="block text-[10px] uppercase font-bold tracking-wider text-[#A0B8C7]/70 mb-1.5">Color</label>
                        <select
                          value={sel.color_code || ''}
                          onChange={(e) => setSelection(slot.key, { color_code: e.target.value, product_id: slot.product_id })}
                          className="w-full px-3 py-2 rounded-lg bg-[#0F2A3D] border border-[#1E4159] text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                          data-testid={`bundle-slot-${slot.key}-color`}
                        >
                          <option value="">— Select color —</option>
                          {slot.colors.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-[#A0B8C7]/60 mt-2" style={{ fontFamily: 'Manrope' }}>
            Each item in your bundle can be a different size{requireColors ? ' and color' : ''}.
          </p>
        </div>
      )}

      {upgrades.length > 0 && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#4A90A4] mb-2 block" style={{ fontFamily: 'Outfit' }}>
            Optional upgrades
          </label>
          <div className="space-y-1.5">
            {upgrades.map((u) => {
              const sel = getUpgrade(u.label);
              return (
                <div key={u.label}
                  className={`px-4 py-2.5 rounded-xl border transition-all ${sel.selected ? 'border-[#F5A623]/30 bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29]'}`}
                  data-testid={`bundle-upgrade-${u.label.replace(/\s+/g, '-').toLowerCase()}`}>
                  <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="flex items-center gap-3">
                      <input type="checkbox" checked={!!sel.selected}
                        onChange={(e) => setUpgrade(u.label, { selected: e.target.checked })}
                        className="accent-[#F5A623]" />
                      <span className="text-sm text-white" style={{ fontFamily: 'Manrope' }}>{u.label}</span>
                    </span>
                    <span className="text-xs text-[#F5A623] font-bold">+${(u.price || 0).toFixed(2)}</span>
                  </label>
                  {sel.selected && u.requires_text_input && (
                    <input type="text" placeholder="Enter the text for this upgrade (e.g. name)"
                      value={sel.text_value || ''}
                      onChange={(e) => setUpgrade(u.label, { text_value: e.target.value })}
                      className="mt-2 w-full px-3 py-2 rounded-lg bg-[#0A1C29] border border-[#1E4159] text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                      data-testid={`bundle-upgrade-text-${u.label.replace(/\s+/g, '-').toLowerCase()}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Single Item Configuration Card ── */
const ItemConfigCard = ({ item, options, orderId, token, onSave }) => {
  const isBundle = item.product_kind === 'school_uniform_bundle';
  const schoolArtworkProvided = item.school_artwork_provided === true
    || (item.product_kind || '').startsWith('school_uniform');
  const [expanded, setExpanded] = useState(true);
  const [sizeBreakdown, setSizeBreakdown] = useState([]);
  const [colorBreakdown, setColorBreakdown] = useState([]);
  const [logoType, setLogoType] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [bundleSelections, setBundleSelections] = useState(null);
  const [saving, setSaving] = useState(false);

  // Load existing config
  useEffect(() => {
    if (item.config) {
      const cfg = item.config;
      if (cfg.size_breakdown) setSizeBreakdown(cfg.size_breakdown.map(s => ({ code: s.size_code, quantity: s.quantity })));
      if (cfg.color_breakdown) setColorBreakdown(cfg.color_breakdown.map(c => ({ code: c.color_code, quantity: c.quantity })));
      if (cfg.logo_type) setLogoType(cfg.logo_type);
      if (cfg.logo_url) setLogoUrl(cfg.logo_url);
      if (cfg.bundle_selections) setBundleSelections(cfg.bundle_selections);
    }
  }, [item.config]);

  const sizesComplete = sizeBreakdown.reduce((s, b) => s + b.quantity, 0) === item.quantity;
  const colorsComplete = colorBreakdown.reduce((s, b) => s + b.quantity, 0) === item.quantity;
  // Bundles: every slot needs a size (+ color if Northwest), and required-text upgrades must be filled.
  const bundleSchoolSlug = item.bundle_school_slug || '';
  const bundleRequiresColors = bundleSchoolSlug.toLowerCase().includes('northwest');
  const bundleSlotCount = isBundle
    ? (item.bundle_items || []).reduce((s, bi) => s + (bi.quantity || 1), 0) * (item.quantity || 1)
    : 0;
  const bundleComplete = !isBundle || (() => {
    const upgrades = item.bundle_config?.upgrades || [];
    const sel = bundleSelections?.upgrades || [];
    const itemSels = bundleSelections?.item_selections || [];
    // 1. Every expected slot has a size, and (if required) a color.
    if (itemSels.length < bundleSlotCount) return false;
    const allSlotsFilled = itemSels.every((s) => {
      if (!s.size_code) return false;
      if (bundleRequiresColors && !s.color_code) return false;
      return true;
    });
    if (!allSlotsFilled) return false;
    // 2. Required-text upgrades that are selected need text.
    return upgrades.every((u) => {
      if (!u.requires_text_input) return true;
      const chosen = sel.find((s) => s.label === u.label);
      return !chosen?.selected || (chosen.text_value && chosen.text_value.trim().length > 0);
    });
  })();
  // For bundles, sizes & colors are captured per slot via bundle_selections, so the
  // legacy aggregate breakdowns are ignored when computing completeness.
  const isComplete = isBundle
    ? bundleComplete
    : (sizesComplete && colorsComplete);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/order-config/order/${orderId}/item/${item.id}`, {
        size_breakdown: isBundle ? [] : sizeBreakdown.filter(s => s.quantity > 0).map(s => ({ size_code: s.code, quantity: s.quantity })),
        color_breakdown: isBundle ? [] : colorBreakdown.filter(c => c.quantity > 0).map(c => ({ color_code: c.code, quantity: c.quantity })),
        logo_type: logoType || 'none',
        logo_url: logoUrl || null,
        bundle_selections: isBundle ? bundleSelections : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${item.product_name} configured`);
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const statusColor = isComplete ? 'bg-[#34C759]' : item.config?.status === 'partial' ? 'bg-[#F5A623]' : 'bg-[#A0B8C7]/30';

  return (
    <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] overflow-hidden" data-testid={`config-item-${item.id}`}>
      <div className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-[#163852] transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${statusColor}`} />
          <div>
            <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{item.product_name}</p>
            <p className="text-xs text-[#A0B8C7]/60">Qty: {item.quantity} · {isComplete ? 'Complete' : 'Needs configuration'}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#A0B8C7]/40" /> : <ChevronDown className="w-4 h-4 text-[#A0B8C7]/40" />}
      </div>

      {expanded && (
        <div className="px-5 pb-5 space-y-6 border-t border-[#1E4159]/50 pt-4">
          {/* Bundle config — per-item size (+ color when school requires) + upgrades */}
          {isBundle && (
            <BundleConfigurator
              bundleConfig={item.bundle_config}
              bundleItems={item.bundle_items}
              schoolSlug={item.bundle_school_slug}
              totalQty={item.quantity}
              value={bundleSelections}
              onChange={setBundleSelections}
            />
          )}

          {/* Sizes (non-bundle only — bundles use per-slot selection above) */}
          {!isBundle && options.sizes.length > 0 && (
            <BreakdownGrid label="Size Breakdown" options={options.sizes.map(s => ({ code: s.code, label: s.label }))}
              breakdown={sizeBreakdown} total={item.quantity} onChange={setSizeBreakdown} />
          )}

          {/* Colors (non-bundle only) */}
          {!isBundle && options.colors.length > 0 && (
            <BreakdownGrid label="Color Breakdown" options={options.colors.map(c => ({ code: c.code, label: c.label, hex: c.hex }))}
              breakdown={colorBreakdown} total={item.quantity} onChange={setColorBreakdown} />
          )}

          {/* Logo */}
          {!schoolArtworkProvided && (
            <LogoSelector logoType={logoType} logoUrl={logoUrl}
              onTypeChange={setLogoType} onUrlChange={setLogoUrl}
              orderId={orderId} token={token} />
          )}

          {/* Save */}
          <button onClick={handleSave} disabled={saving || !isComplete}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all
              bg-[#F5A623] text-[#0A1C29] hover:bg-[#D88F1B] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`save-config-${item.id}`}
            style={{ fontFamily: 'Outfit' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? 'Saving...' : isComplete ? 'Save Configuration' : (isBundle ? 'Pick a size for every item to continue' : `Assign all ${item.quantity} items to continue`)}
          </button>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════
   Order Configuration Page
   ══════════════════════════════════ */
const OrderConfigPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { customer, token } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [optionsCache, setOptionsCache] = useState({});
  const [error, setError] = useState('');

  const loadOrder = async () => {
    try {
      const res = await axios.get(`${API}/api/order-config/order/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderData(res.data);

      // Load product options for each shop item's product type
      const types = new Set();
      for (const item of res.data.items) {
        const pt = item.customization_options?.product_type;
        if (pt && !item.is_byo && !optionsCache[pt]) types.add(pt);
      }
      const newCache = { ...optionsCache };
      for (const pt of types) {
        const optRes = await axios.get(`${API}/api/order-config/options/${pt}`);
        newCache[pt] = optRes.data;
      }
      setOptionsCache(newCache);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load order');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) loadOrder(); }, [token, orderId]);

  if (loading) {
    return <div className="min-h-screen bg-[#0A1C29] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#F5A623] animate-spin" /></div>;
  }

  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-[#0A1C29] flex items-center justify-center p-4">
        <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-white font-semibold mb-2">{error || 'Order not found'}</p>
          <Link to="/portal/dashboard" className="text-[#F5A623] text-sm hover:underline">Back to Portal</Link>
        </div>
      </div>
    );
  }

  const shopItems = orderData.items.filter(i => !i.is_byo);
  const byoItems = orderData.items.filter(i => i.is_byo);

  return (
    <div className="min-h-screen bg-[#0A1C29]" data-testid="order-config-page">
      {/* Top bar */}
      <div className="bg-[#0F2A3D] border-b border-[#1E4159]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/portal/dashboard" className="flex items-center gap-2 text-[#A0B8C7] hover:text-white text-sm transition" style={{ fontFamily: 'Manrope' }}>
            <ArrowLeft className="w-4 h-4" /> Back to Portal
          </Link>
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Configure Order</h1>
          <span className="text-xs text-[#F5A623] font-semibold">{orderData.order_number}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Set up your items</h2>
          <p className="text-sm text-[#A0B8C7] mt-1" style={{ fontFamily: 'Manrope' }}>
            {orderData.items.every((item) => item.school_artwork_provided === true
              || (item.product_kind || '').startsWith('school_uniform'))
              ? 'Choose the required sizes and colors before we begin production.'
              : 'Choose sizes, colors, and provide artwork where requested before we begin production.'}
          </p>
        </div>

        {/* Shop items that need configuration */}
        {shopItems.length > 0 && (
          <div className="space-y-4 mb-8">
            {shopItems.map(item => {
              const pt = item.customization_options?.product_type;
              const opts = optionsCache[pt] || { sizes: [], colors: [] };
              return (
                <ItemConfigCard key={item.id} item={item} options={opts}
                  orderId={orderId} token={token} onSave={loadOrder} />
              );
            })}
          </div>
        )}

        {/* BYO items — just show them, no size/color needed */}
        {byoItems.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A90A4] mb-3" style={{ fontFamily: 'Outfit' }}>
              Your Items (Bring Your Own)
            </h3>
            <div className="space-y-2">
              {byoItems.map(item => (
                <div key={item.id} className="bg-[#0F2A3D] rounded-xl border border-[#1E4159] p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white" style={{ fontFamily: 'Manrope' }}>{item.product_name}</p>
                    <p className="text-xs text-[#A0B8C7]/60">Qty: {item.quantity} — You'll provide these items</p>
                  </div>
                  {!(item.school_artwork_provided === true || (item.product_kind || '').startsWith('school_uniform')) && <div className="flex items-center gap-2">
                    <LogoSelector logoType={item.config?.logo_type} logoUrl={item.config?.logo_url}
                      onTypeChange={async (type) => {
                        await axios.put(`${API}/api/order-config/order/${orderId}/item/${item.id}`,
                          { logo_type: type }, { headers: { Authorization: `Bearer ${token}` } });
                        loadOrder();
                      }}
                      onUrlChange={async (url) => {
                        await axios.put(`${API}/api/order-config/order/${orderId}/item/${item.id}`,
                          { logo_type: 'upload', logo_url: url }, { headers: { Authorization: `Bearer ${token}` } });
                        loadOrder();
                      }}
                      orderId={orderId} token={token} />
                  </div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status summary */}
        <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-5 text-center">
          {orderData.all_configured ? (
            <>
              <div className="w-14 h-14 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-3">
                <Check className="w-7 h-7 text-[#34C759]" />
              </div>
              <p className="text-white font-bold text-lg mb-1" style={{ fontFamily: 'Outfit' }}>All items configured!</p>
              <p className="text-sm text-[#A0B8C7] mb-4" style={{ fontFamily: 'Manrope' }}>Your order is ready for production.</p>
              <button onClick={() => navigate('/portal/dashboard')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#F5A623] text-[#0A1C29] font-bold rounded-xl text-sm hover:bg-[#D88F1B] transition"
                style={{ fontFamily: 'Outfit' }} data-testid="done-btn">
                <Package className="w-4 h-4" /> Go to Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <p className="text-[#F5A623] font-semibold text-sm mb-2" style={{ fontFamily: 'Manrope' }}>
                {shopItems.filter(i => !(i.config?.status === 'complete')).length} item{shopItems.filter(i => !(i.config?.status === 'complete')).length !== 1 ? 's' : ''} still need configuration
              </p>
              <button onClick={() => navigate('/portal/dashboard')}
                className="text-sm text-[#A0B8C7] hover:text-white transition"
                style={{ fontFamily: 'Manrope' }} data-testid="finish-later-btn">
                Finish later in your portal
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfigPage;
